import { fetchUserConfigs } from "@/automation/system-config/systemConfigApi";
import { isVisibleConfig } from "@/components/experiments/flags";
import type {
  ConfigStep as ConfigStepDefinition,
  ConfigParam,
  LabwareType,
  Head,
  Robot,
  LiquidClass,
} from "@/automation/system-config/systemConfigParser";
import type { DeckConfiguration } from "@/state/run/useDeckLayoutState";
import { ref, computed } from "vue";
import {
  doc,
  setDoc as setFirebaseDoc,
  deleteDoc as deleteFirebaseDoc,
} from "firebase/firestore";
import { db } from "@/firebase";
import { useCurrentUser } from "vuefire";
import { collection } from "firebase/firestore";
import { getDocs } from "firebase/firestore";

export type ConfigStep = ConfigStepDefinition;
export type ConfigParameter = ConfigParam;

export interface SystemConfig {
  name: string;
  configText: string;
  createdAt: Date;
  createdBy: string;
  labware: LabwareType[];
  heads: Head[];
  robots: Robot[];
  steps: ConfigStep[];
  deckLayouts: DeckConfiguration[];
  liquidClasses: LiquidClass[];
  workspaceId: string;
}

export interface ConfigMetadata {
  id: string;
  name: string;
  createdAt: Date;
  createdBy: string;
  // True for configs generated by the catalog screen; absent/false for legacy
  // file-uploaded configs. Used by `isVisibleConfig` to hide legacy ones.
  generatedFromCatalog?: boolean;
}

/**
 * These are the globally available states managed by the config state composable.
 */
const activeConfig = ref<SystemConfig | undefined>(undefined);
const activeRobot = ref<Robot | null>(null);
const activeHead = ref<Head | null>(null);
const availableConfigs = ref<ConfigMetadata[]>([]);
const showConfigDialog = ref<boolean>(false);

/**
 * Global state store for system configuration related state
 */
export function useConfigState() {
  const user = useCurrentUser();

  async function loadUserConfigs(workspaceId: string) {
    try {
      const configs = await fetchUserConfigs(workspaceId);
      // Hide legacy file-uploaded configs when the flag is on. Every picker and
      // the quick-create auto-select read this singleton, so they hide in
      // lock-step. Flag off → isVisibleConfig keeps everything (no-op).
      const visibleConfigs = configs.filter(isVisibleConfig);
      setAvailableConfigs(visibleConfigs);
      return visibleConfigs;
    } catch (error) {
      console.error("Error loading configs:", error);
      throw error;
    }
  }

  function setActiveConfig(config: SystemConfig | undefined) {
    activeConfig.value = config;

    // Reset active robot when config changes
    if (config && config.robots && config.robots.length > 0) {
      activeRobot.value = config.robots[0];
    } else {
      activeRobot.value = null;
    }
  }

  function setActiveRobot(robotId: string) {
    if (!activeConfig.value) return;

    const robot = activeConfig.value.robots.find((r) => r.id === robotId);
    if (robot) {
      activeRobot.value = robot;
    }
  }

  /**
   * Sets the active head for the current protocol.
   * If headId is null/undefined, auto-selects the first available head.
   */
  function setActiveHead(headId: string | null | undefined) {
    if (!activeConfig.value || !headId) {
      // Auto-select first head available
      if (activeConfig.value?.heads?.length) {
        activeHead.value = activeConfig.value.heads[0];
      } else {
        activeHead.value = null;
      }
      return;
    }

    const head = activeConfig.value.heads.find((h) => h.id === headId);
    if (head) {
      activeHead.value = head;
    } else {
      activeHead.value = null;
    }
  }

  function setAvailableConfigs(configs: ConfigMetadata[]) {
    availableConfigs.value = configs;
  }

  /**
   * Sets the visibility of the config dialog
   */
  function setShowConfigDialog(show: boolean) {
    showConfigDialog.value = show;
  }

  /**
   * Adds a new system configuration to the specified workspace
   * @param workspaceId The workspace ID to add the config to
   * @param name The name of the configuration
   * @param configText The XML/text content of the configuration
   */
  async function addSystemConfig(
    workspaceId: string,
    name: string,
    configText: string
  ) {
    try {
      // Create the document in Firestore
      await setFirebaseDoc(
        doc(db, `workspaces/${workspaceId}/system_configs/${name}`),
        {
          name,
          configText,
          createdAt: new Date(),
          createdBy: user.value?.uid,
          workspaceId,
        }
      );

      await loadUserConfigs(workspaceId);

      return true;
    } catch (error) {
      console.error("Error adding system config:", error);
      throw error;
    }
  }

  /**
   * Deletes a system configuration from the specified workspace
   * @param workspaceId The workspace ID to delete the config from
   * @param configName The name of the configuration to delete
   */
  async function deleteSystemConfig(workspaceId: string, configName: string) {
    try {
      await deleteFirebaseDoc(
        doc(db, `workspaces/${workspaceId}/system_configs/${configName}`)
      );

      await loadUserConfigs(workspaceId);

      return true;
    } catch (error) {
      console.error("Error deleting system config:", error);
      throw error;
    }
  }

  /**
   * Fetches all system configurations for a workspace
   * @param workspaceId The workspace ID to fetch configs for
   * @returns Promise with the list of system configs
   */
  async function fetchSystemConfigs(workspaceId: string) {
    try {
      const configsSnapshot = await getDocs(
        collection(db, `workspaces/${workspaceId}/system_configs`)
      );

      const configs = configsSnapshot.docs.map((doc) => ({
        ...doc.data(),
        name: doc.id,
      }));

      return configs;
    } catch (error) {
      console.error("Error fetching system configs:", error);
      throw error;
    }
  }

  /**
   * Gets a collection reference for system configs
   * This can be used with useCollection in components that need reactivity
   * @param workspaceId The workspace ID
   * @returns A Firestore collection reference
   */
  function getSystemConfigsCollectionRef(workspaceId: string) {
    return collection(db, `workspaces/${workspaceId}/system_configs`);
  }

  /**
   * Steps filtered by the active head.
   * If no head is selected or a step has no compatibleHeads restriction,
   * the step is included.
   */
  const filteredSteps = computed<ConfigStepDefinition[]>(() => {
    const steps = activeConfig.value?.steps ?? [];
    const headId = activeHead.value?.id;
    if (!headId) return steps;

    return steps.filter((step) => {
      // No restriction means compatible with all heads
      if (!step.compatibleHeads || step.compatibleHeads.length === 0) return true;
      return step.compatibleHeads.includes(headId);
    });
  });

  return {
    // State
    activeConfig,
    activeRobot,
    activeHead,
    availableConfigs,
    showConfigDialog,
    filteredSteps,

    // Methods
    loadUserConfigs,
    setActiveConfig,
    setActiveRobot,
    setActiveHead,
    setAvailableConfigs,
    setShowConfigDialog,
    addSystemConfig,
    deleteSystemConfig,
    fetchSystemConfigs,
    getSystemConfigsCollectionRef,
  };
}
